import { defineStore } from 'pinia'
import { ref } from 'vue'
import api from '../api'
import { useUserStore } from './userStore'
import { useBandStore } from './bandStore'
import { useBandEventStore } from './bandEventStore'
import { usePresenceStore } from './presenceStore'
import type { SetlistSummaryDto, SetlistDto, SetlistSongDto, CreateSetlistDto, AddSetlistSongDto, UpdateSetlistDto, ReorderSetlistSongsDto } from '../dtos'

export const useSetlistStore = defineStore('setlist', () => {
    const userStore = useUserStore()
    const bandStore = useBandStore()

    const setlists = ref<SetlistSummaryDto[]>([])
    const currentSetlist = ref<SetlistDto | null>(null)
    const loading = ref(false)
    const detailLoading = ref(false)
    const isFetching = ref(false)

    async function fetchBandSetlists(bandId: number) {
        if (!userStore.user || isFetching.value) return
        loading.value = true
        isFetching.value = true
        setlists.value = []
        try {
            const token = await userStore.user.getIdToken()
            const response = await api.get(`/setlists/band/${bandId}`, {
                headers: { Authorization: `Bearer ${token}` }
            })
            setlists.value = response.data
        } catch (error) {
            console.error('Failed to fetch setlists:', error)
        } finally {
            loading.value = false
            isFetching.value = false
        }
    }

    async function fetchSetlistDetails(setlistId: number) {
        if (!userStore.user) return
        detailLoading.value = true
        try {
            const token = await userStore.user.getIdToken()
            const response = await api.get(`/setlists/${setlistId}`, {
                headers: { Authorization: `Bearer ${token}` }
            })
            currentSetlist.value = response.data

            // Start presence tracking
            const presenceStore = usePresenceStore()
            presenceStore.startPresence('Setlist', setlistId)
        } catch (error) {
            console.error('Failed to fetch setlist details:', error)
        } finally {
            detailLoading.value = false
        }
    }

    async function addSetlist(name: string) {
        if (!userStore.user || !bandStore.currentBand) return
        try {
            const token = await userStore.user.getIdToken()
            const payload: CreateSetlistDto = {
                name,
                bandId: bandStore.currentBand.id
            }
            const response = await api.post('/setlists', payload, {
                headers: { Authorization: `Bearer ${token}` }
            })
            const summary: SetlistSummaryDto = {
                id: response.data.id,
                name: response.data.name,
                bandId: response.data.bandId,
                songCount: 0,
                updatedAt: response.data.updatedAt,
                rowVersion: response.data.rowVersion
            }
            setlists.value.push(summary)
            setlists.value.sort((a, b) => a.name.localeCompare(b.name))
            return response.data
        } catch (error) {
            console.error('Failed to add setlist:', error)
            throw error
        }
    }

    async function updateSetlist(setlistId: number, data: { name?: string }) {
        if (!userStore.user || !currentSetlist.value) return
        try {
            const token = await userStore.user.getIdToken()
            const payload: UpdateSetlistDto = {
                ...data,
                rowVersion: currentSetlist.value.rowVersion
            }
            const response = await api.put(`/setlists/${setlistId}`, payload, {
                headers: { Authorization: `Bearer ${token}` }
            })
            currentSetlist.value = response.data
            const idx = setlists.value.findIndex(s => s.id === setlistId)
            if (idx !== -1) {
                setlists.value[idx]!.name = response.data.name
                setlists.value[idx]!.rowVersion = response.data.rowVersion
            }
            return response.data
        } catch (error: any) {
            if (error.response?.status === 409 && currentSetlist.value) {
                error._handled = true
                const { useUiStore } = await import('./uiStore')
                const uiStore = useUiStore()
                const id = currentSetlist.value.id
                uiStore.triggerConcurrencyError(
                    error.response.data || 'The setlist was modified by another user.',
                    true,
                    async () => { await fetchSetlistDetails(id) }
                )
                return
            }
            console.error('Failed to update setlist:', error)
            throw error
        }
    }

    async function deleteSetlist(setlistId: number) {
        if (!userStore.user) return
        try {
            const token = await userStore.user.getIdToken()
            await api.delete(`/setlists/${setlistId}`, {
                headers: { Authorization: `Bearer ${token}` }
            })
            setlists.value = setlists.value.filter(s => s.id !== setlistId)
            // Clear setlist reference from any linked events
            const bandEventStore = useBandEventStore()
            bandEventStore.events.forEach(e => {
                if (e.setlistId === setlistId) {
                    e.setlistId = undefined
                    e.setlistName = undefined
                }
            })
        } catch (error) {
            console.error('Failed to delete setlist:', error)
            throw error
        }
    }

    async function duplicateSetlist(setlistId: number) {
        if (!userStore.user) return
        try {
            const token = await userStore.user.getIdToken()
            const response = await api.post(`/setlists/${setlistId}/duplicate`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            })
            const summary: SetlistSummaryDto = {
                id: response.data.id,
                name: response.data.name,
                bandId: response.data.bandId,
                songCount: response.data.songs?.length || 0,
                updatedAt: response.data.updatedAt,
                rowVersion: response.data.rowVersion
            }
            setlists.value.push(summary)
            setlists.value.sort((a, b) => a.name.localeCompare(b.name))
            return response.data
        } catch (error) {
            console.error('Failed to duplicate setlist:', error)
            throw error
        }
    }

    async function addSongToSetlist(setlistId: number, songId: number) {
        if (!userStore.user || !currentSetlist.value) return
        try {
            const token = await userStore.user.getIdToken()
            const payload: AddSetlistSongDto = { songId, rowVersion: currentSetlist.value.rowVersion }
            const response = await api.post(`/setlists/${setlistId}/songs`, payload, {
                headers: { Authorization: `Bearer ${token}` }
            })
            currentSetlist.value.songs.push(response.data)
            // Update song count in summary list
            const idx = setlists.value.findIndex(s => s.id === setlistId)
            if (idx !== -1) setlists.value[idx]!.songCount++
            return response.data as SetlistSongDto
        } catch (error: any) {
            if (error.response?.status === 409 && currentSetlist.value) {
                error._handled = true
                const { useUiStore } = await import('./uiStore')
                const uiStore = useUiStore()
                const id = currentSetlist.value.id
                uiStore.triggerConcurrencyError(
                    error.response.data || 'The setlist was modified by another user.',
                    true,
                    async () => { await fetchSetlistDetails(id) }
                )
                return
            }
            console.error('Failed to add song to setlist:', error)
            throw error
        }
    }

    async function removeSongFromSetlist(setlistSongId: number) {
        if (!userStore.user || !currentSetlist.value) return
        try {
            const token = await userStore.user.getIdToken()
            await api.delete(`/setlists/songs/${setlistSongId}`, {
                headers: { Authorization: `Bearer ${token}` }
            })
            const setlistId = currentSetlist.value.id
            currentSetlist.value.songs = currentSetlist.value.songs.filter(s => s.id !== setlistSongId)
            currentSetlist.value.songs.forEach((s, i) => s.sequenceNumber = i + 1)
            const idx = setlists.value.findIndex(s => s.id === setlistId)
            if (idx !== -1 && setlists.value[idx]!.songCount > 0) setlists.value[idx]!.songCount--
        } catch (error) {
            console.error('Failed to remove song from setlist:', error)
            throw error
        }
    }

    async function reorderSetlistSongs(setlistId: number, setlistSongIds: number[]) {
        if (!userStore.user) return
        try {
            const token = await userStore.user.getIdToken()
            const payload: ReorderSetlistSongsDto = {
                setlistSongIds,
                rowVersion: currentSetlist.value?.rowVersion
            }
            await api.post(`/setlists/${setlistId}/reorder`, payload, {
                headers: { Authorization: `Bearer ${token}` }
            })
            if (currentSetlist.value && currentSetlist.value.id === setlistId) {
                setlistSongIds.forEach((id, index) => {
                    const song = currentSetlist.value?.songs.find(s => s.id === id)
                    if (song) song.sequenceNumber = index + 1
                })
                currentSetlist.value.songs.sort((a, b) => a.sequenceNumber - b.sequenceNumber)
            }
        } catch (error: any) {
            if (error.response?.status === 409 && currentSetlist.value) {
                error._handled = true
                const { useUiStore } = await import('./uiStore')
                const uiStore = useUiStore()
                const id = currentSetlist.value.id
                uiStore.triggerConcurrencyError(
                    error.response.data || 'The setlist was modified by another user.',
                    true,
                    async () => { await fetchSetlistDetails(id) }
                )
                return
            }
            console.error('Failed to reorder setlist songs:', error)
            throw error
        }
    }

    async function voteOnSong(setlistSongId: number, isLike: boolean) {
        if (!userStore.user || !currentSetlist.value) return
        try {
            const token = await userStore.user.getIdToken()
            const response = await api.post(`/setlists/songs/${setlistSongId}/vote?isLike=${isLike}`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            })
            const updated = response.data as SetlistSongDto
            const idx = currentSetlist.value.songs.findIndex(s => s.id === setlistSongId)
            if (idx !== -1) {
                currentSetlist.value.songs[idx] = updated
            }
        } catch (error) {
            console.error('Failed to vote on song:', error)
            throw error
        }
    }

    function clear() {
        setlists.value = []
        currentSetlist.value = null
        loading.value = false
        detailLoading.value = false
        isFetching.value = false
    }

    return {
        setlists, currentSetlist, loading, detailLoading, isFetching,
        fetchBandSetlists, fetchSetlistDetails, addSetlist, updateSetlist, deleteSetlist, duplicateSetlist,
        addSongToSetlist, removeSongFromSetlist, reorderSetlistSongs, voteOnSong, clear
    }
})
